import { ChangeDetectorRef, Component, inject, input, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CommonAPI } from '@app/core/api/common.service';
import { DisplayToast } from '@app/core/store/states/ui/ui-state.actions';
import { getValueByPath } from '@app/core/utilities/object.utilities';
import { refinersToUrl } from '@app/core/utilities/refiners.utilities';
import { HeapDirective } from '@app/shared/directives/heap.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzNotificationComponent, NzNotificationService } from 'ng-zorro-antd/notification';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { TableLayoutColumn } from '../../modules/table-layout/models/table-layout.model';
import { TableLayoutService } from '../../modules/table-layout/services/table-layout.service';
import { lastValue } from '../../operators/last-value.operator';
import { IconComponent } from '../icon/icon.component';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

export interface ExportConfig {
  fields: string[];
  columns?: string[];
  limit: number;
  url: string;
  refiners: [string, string][];
}

@Component({
  selector: 'adspt-export-table-data',
  templateUrl: './export-table-data.component.html',
  styleUrls: ['./export-table-data.component.scss'],
  imports: [NzButtonModule, NzDropDownModule, IconComponent, TranslatePipe, HeapDirective, NzSpinComponent],
})
export class ExportTableDataComponent implements OnDestroy {
  private translate = inject(TranslateService);
  private notificationService = inject(NzNotificationService);
  private commonAPI = inject(CommonAPI);
  private cd = inject(ChangeDetectorRef);
  private tl = inject(TableLayoutService, { optional: true });

  @ViewChild('template', { static: false }) template: TemplateRef<{ $implicit: NzNotificationComponent; data: any }>;
  @ViewChild('limitTemplate', { static: false }) limitTmpl: TemplateRef<{
    $implicit: NzNotificationComponent;
    data: any;
  }>;
  readonly heapData = input({});
  readonly icon = input<string>('download');
  readonly useConfig = input<boolean>(undefined);
  readonly config = input<ExportConfig>(<ExportConfig>{});

  readonly columnsTranslations = this.translate.instant('export-column-names');
  readonly export = [{ key: 'csv' }, { key: 'xls' }] as any;
  subs$: Subscription[] = [];

  @Dispatch() toast = () =>
    new DisplayToast({ title: this.translate.instant('common.error-msg'), type: 'error', duration: 3000 });

  onExport(item: any) {
    if (this.config().limit > 100000) {
      return this.notificationService.template(this.limitTmpl, {
        nzDuration: 5000,
        nzPauseOnHover: true,
        nzAnimate: true,
        nzClass: 'adspt-toast error left',
      });
    }
    const temp = this.notificationService.template(this.template, {
      nzDuration: 7500,
      nzPauseOnHover: true,
      nzAnimate: false,
      nzClass: 'adspt-toast info left',
    });
    temp.messageId = (+new Date()).toString();

    const layout = this.tl && lastValue(this.tl.layout$);

    const configValue = this.config();
    if (!configValue.columns) {
      configValue.columns = configValue.fields.map((field) => getValueByPath(this.columnsTranslations, field) || '');
    }

    const config = !this.useConfig() && layout?.columns.length ? this.getConfigLayout(layout.columns) : configValue;
    const refiners = refinersToUrl(config.refiners);
    this.subs$.push(
      this.commonAPI
        .export(
          `${config.url}?${refiners ? refiners + '&' : ''}limit=${config.limit || 100000}`,
          config.fields.map((d) => d.replace('^', '.')),
          config.columns,
          item.key,
        )
        .pipe(take(1))
        .subscribe(
          () => {
            setTimeout(() => {
              this.notificationService.remove(temp.messageId);
              this.cd.detectChanges();
            }, 1000);
          },
          () => {
            this.notificationService.remove(temp.messageId);
            this.toast();
            this.cd.detectChanges();
          },
        ),
    );
  }

  constructor() {
    const chartExports = this.translate.instant('common.chart-exports');
    this.export.forEach((item) => (item.name = chartExports[item.key]));
  }
  ngOnDestroy() {
    this.subs$.forEach((s) => s.unsubscribe());
  }

  getExtraHeapData(data: any): any {
    return {
      ...(this.heapData() || {}),
      ...data,
    };
  }

  private getConfigLayout(columns: TableLayoutColumn[]): any {
    const baseColsIdx = this.config().fields.reduce((indices, field, idx) => {
      if (columns.every(({ id }) => field !== id && field !== `${id}_adjusted`)) {
        indices.push(idx);
      }

      return indices;
    }, []);
    const visibleColumns = columns.filter((col) => col.visible);
    const visibleOrderedFields = visibleColumns.map((col) =>
      this.config().fields.includes(`${col.id}_adjusted`) ? `${col.id}_adjusted` : col.id,
    );
    const visibleOrderedColumns = visibleOrderedFields.map(
      (field, fieldIdx) =>
        this.config().columns[this.config().fields.indexOf(field)] ||
        this.translate.instant(visibleColumns[fieldIdx].title),
    );

    return {
      ...this.config(),
      fields: [...baseColsIdx.map((idx) => this.config().fields[idx]), ...visibleOrderedFields],
      columns: [...baseColsIdx.map((idx) => this.config().columns[idx]), ...visibleOrderedColumns],
    };
  }
}
